import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, mergeProps as _mergeProps, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "automation-wizard-field-select" }

import { ref, computed, watch, onMounted, inject } from 'vue';
import type { Translator } from 'js/shims-vue';
import {
    getStaffs,
    getTaskLabels,
    getOpportunitySources,
} from 'api/automation/automationService.js';

interface SelectFieldKeyValue {
    key: string | number;
    value: string;
}

interface SelectFieldProps {
    formItem?: Record<string, unknown>;
    select?: Record<string, unknown> & { multiple?: boolean };
    mappedFields?: Record<string, string>;
}

interface SelectField {
    id: string;
    type: string;
    required?: boolean;
    disabled?: boolean;
    currentError?: string;
    objectType?: string;
    values?: SelectFieldKeyValue[];
    prependValues?: SelectFieldKeyValue[];
    props?: SelectFieldProps;
}

interface SelectOption {
    label: string;
    value: string | number;
    dotColor?: string;
    type?: string;
    isDefault?: boolean;
}

type ModelValue = string | number | Record<string, unknown> | (string | number)[];

interface Staff {
    id: number;
    firstname: string;
    lastname: string;
}

interface TaskLabel {
    id: number;
    name: string;
    color: string | null;
    is_active: boolean;
}

interface OpportunitySource {
    id: number;
    name: string;
    is_default: boolean;
}

interface Props {
    field: SelectField;
    modelValue?: ModelValue;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AutomationWizardFieldSelect',
  props: {
    field: {},
    modelValue: { default: '' }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $t = inject('$t') as Translator;

const options = ref<SelectOption[]>([]);
const loading = ref(false);

const formItemProps = computed<Record<string, unknown>>(() => ({
    ...props.field.props?.formItem,
    label: (props.field.props?.formItem?.label as string) || '',
    required: props.field.required || false,
    requiredLabel:
        (props.field.props?.formItem?.requiredLabel as string) || $t.__('common.labels.required'),
}));

const selectProps = computed<Record<string, unknown>>(() => ({
    ...props.field.props?.select,
}));

const disabled = computed<boolean>(() => props.field.disabled || false);

const showSkeleton = computed<boolean>(() => loading.value && options.value.length === 0);

const validOptionValues = computed<Set<string | number>>(
    () => new Set(options.value.map((option) => option.value)),
);

const filterValidValues = (values: unknown): (string | number)[] => {
    if (!Array.isArray(values)) {
        return [];
    }
    return values.filter(
        (value) => value !== null && value !== undefined && validOptionValues.value.has(value),
    );
};

const currentValue = computed<ModelValue>(() => {
    if (selectProps.value?.multiple) {
        return filterValidValues(props.modelValue);
    }
    return validOptionValues.value.has(props.modelValue as string | number) ? props.modelValue : '';
});

const handleValueChange = (newValue: ModelValue): void => {
    const cleanValue = selectProps.value?.multiple
        ? filterValidValues(newValue)
        : validOptionValues.value.has(newValue as string | number)
          ? newValue
          : '';
    emit('update:modelValue', cleanValue);
};

const loadStaffsOptions = async (): Promise<SelectOption[]> => {
    const response = await getStaffs();
    return (response.data as Staff[]).map((staff) => ({
        label: `${staff.firstname} ${staff.lastname}`,
        value: staff.id,
    }));
};

const loadLabelTasksOptions = async (): Promise<SelectOption[]> => {
    const response = await getTaskLabels();
    return (response.data as TaskLabel[])
        .filter((label) => label.is_active)
        .map((label) => ({
            label: label.name,
            value: label.id,
            dotColor: label.color ? `#${label.color}` : '',
            type: 'dot',
        }));
};

const loadOpportunitySourcesOptions = async (): Promise<SelectOption[]> => {
    const response = await getOpportunitySources();
    return (response.data as OpportunitySource[]).map((source) => ({
        label: source.name,
        value: source.id,
        isDefault: !!source.is_default,
    }));
};

const loadOptionsFromApi = async (objectType: string): Promise<SelectOption[]> => {
    switch (objectType) {
        case 'staffs':
            return await loadStaffsOptions();
        case 'labelTask':
            return await loadLabelTasksOptions();
        case 'sourceOpp':
            return await loadOpportunitySourcesOptions();
        default:
            return [];
    }
};

const loadOptions = async (): Promise<void> => {
    try {
        loading.value = true;

        const prependOptions: SelectOption[] = Array.isArray(props.field.prependValues)
            ? props.field.prependValues.map((item) => ({
                  label: item.value,
                  value: item.key,
              }))
            : [];

        const fieldOptions: SelectOption[] = Array.isArray(props.field.values)
            ? props.field.values.map((item) => ({
                  label: item.value,
                  value: item.key,
              }))
            : [];

        const apiOptions = props.field.objectType
            ? await loadOptionsFromApi(props.field.objectType)
            : [];

        const sortedOptions = [...fieldOptions, ...apiOptions].sort((a, b) =>
            a.label.localeCompare(b.label),
        );

        options.value = [...prependOptions, ...sortedOptions];

        if (props.field.objectType === 'sourceOpp' && !props.modelValue) {
            const defaultOption = apiOptions.find((option) => option.isDefault);
            if (defaultOption) {
                emit('update:modelValue', defaultOption.value);
            }
        }
    } catch (error) {
        options.value = [];
        throw error;
    } finally {
        loading.value = false;
    }
};

watch(
    () => props.modelValue,
    async () => {
        if (options.value.length === 0) {
            await loadOptions();
        }
    },
);

onMounted(async () => {
    await loadOptions();
});

return (_ctx: any,_cache: any) => {
  const _component_CompassSkeleton = _resolveComponent("CompassSkeleton")!
  const _component_CompassDropdownOption = _resolveComponent("CompassDropdownOption")!
  const _component_CompassSelect = _resolveComponent("CompassSelect")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createVNode(_component_CompassSkeleton, {
      size: _ctx.CompassEnums.SkeletonSize.EXTRA_LARGE,
      class: "automation-wizard-field-select__skeleton"
    }, null, 8 /* PROPS */, ["size"]), [
      [_vShow, showSkeleton.value]
    ]),
    _withDirectives(_createVNode(_component_CompassFormItem, _mergeProps(formItemProps.value, {
      error: __props.field.currentError || ''
    }), {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(_component_CompassSelect, _mergeProps(selectProps.value, {
          key: `field-select-${options.value.length}`,
          modelValue: currentValue.value,
          loading: loading.value,
          disabled: disabled.value,
          menuClass: "automation-wizard-field-select",
          "onUpdate:modelValue": handleValueChange
        }), {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
              return (_openBlock(), _createBlock(_component_CompassDropdownOption, {
                key: option.value,
                label: option.label,
                value: option.value,
                dotColor: option.dotColor,
                type: option.type
              }, null, 8 /* PROPS */, ["label", "value", "dotColor", "type"]))
            }), 128 /* KEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        }, 16 /* FULL_PROPS */, ["modelValue", "loading", "disabled"]))
      ]),
      _: 1 /* STABLE */
    }, 16 /* FULL_PROPS */, ["error"]), [
      [_vShow, !showSkeleton.value]
    ])
  ]))
}
}

})